import { type FormEvent, type ReactNode, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { BootstrapDTO, MeDTO } from "@justone/shared";
import { errorMessage, fieldError, http } from "../api/http";
import { keys, useBootstrap, useInviteInfo } from "../api/queries";
import { Field } from "../components/ui";
import { inNativeApp } from "../lib/native";

const GOOGLE_ERRORS: Record<string, string> = {
  google_disabled: "Google 로그인이 설정되지 않았습니다.",
  google_failed: "Google 로그인에 실패했습니다. 다시 시도하세요.",
  not_invited: "이 Google 계정으로 가입한 사용자가 없습니다. 관리자에게 초대를 요청하세요.",
  invite_invalid: "사용할 수 없는 초대입니다. 관리자에게 새 초대를 요청하세요.",
  invite_email_mismatch: "초대받은 이메일과 다른 Google 계정입니다. 초대받은 계정으로 다시 시도하세요.",
  google_in_use: "이 Google 계정은 이미 다른 사용자에게 연결되어 있습니다.",
  link_requires_login: "먼저 로그인하세요.",
};

function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="auth">
      <aside className="auth-brand">
        <div className="wordmark">
          <img src="/icons/favicon.svg" alt="" />
          JustOne
        </div>
        <div>
          <p className="pitch">우리 집과 사무실의 대화, 할일, 기록을 한곳에.</p>
          <p className="pitch-sub">채널에서 이야기하고, 나온 일은 나눠 맡고, 남길 것은 위키에 정리합니다.</p>
        </div>
        <div />
      </aside>
      <main className="auth-main">
        <div className="auth-card">{children}</div>
      </main>
    </div>
  );
}

/** Google은 앱 안의 웹 화면에서 로그인을 막는다 (disallowed_useragent). JustOne 앱에서는 보이지 않는다 */
function GoogleButton({ href, children = "Google 계정으로 계속" }: { href: string; children?: ReactNode }) {
  return (
    <a className="btn" href={href}>
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
      </svg>
      {children}
    </a>
  );
}

/** 로그인·가입 성공: 내 정보를 캐시에 넣고 첫 화면으로 */
function useSignedIn() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return (me: MeDTO) => {
    qc.setQueryData<BootstrapDTO>(keys.bootstrap, (b) => ({
      pushPublicKey: b?.pushPublicKey ?? null,
      needsSetup: false,
      setupCodeRequired: false,
      googleEnabled: b?.googleEnabled ?? false,
      appPush: b?.appPush ?? false,
      iosApp: b?.iosApp ?? null,
      me,
    }));
    navigate("/", { replace: true });
  };
}

/** 아이디·이름·새 비밀번호 입력 (설정과 초대 수락에서 같이 씀) */
function AccountFields({ err, values, set }: { err: unknown; values: { username: string; displayName: string; password: string }; set: (k: "username" | "displayName" | "password", v: string) => void }) {
  return (
    <>
      <Field id="displayName" label="이름" hint="다른 사람에게 보이는 이름입니다." error={fieldError(err, "displayName")}>
        <input id="displayName" name="name" className="input" autoComplete="name" required maxLength={40} value={values.displayName} onChange={(e) => set("displayName", e.target.value)} aria-invalid={!!fieldError(err, "displayName")} />
      </Field>
      <Field id="username" label="아이디" hint="영문 소문자, 숫자, . _ - 로 3~32자" error={fieldError(err, "username")}>
        <input id="username" name="username" type="text" className="input" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} required value={values.username} onChange={(e) => set("username", e.target.value)} aria-invalid={!!fieldError(err, "username")} />
      </Field>
      <Field id="new-password" label="비밀번호" hint="10자 이상" error={fieldError(err, "password")}>
        <input id="new-password" name="password" type="password" className="input" autoComplete="new-password" minLength={10} required value={values.password} onChange={(e) => set("password", e.target.value)} aria-invalid={!!fieldError(err, "password")} />
      </Field>
    </>
  );
}

function useAccountForm() {
  const [values, setValues] = useState({ username: "", displayName: "", password: "" });
  return { values, set: (k: keyof typeof values, v: string) => setValues((s) => ({ ...s, [k]: v })) };
}

export function SetupPage() {
  const boot = useBootstrap();
  const { values, set } = useAccountForm();
  const [setupCode, setSetupCode] = useState("");
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const signedIn = useSignedIn();
  if (boot.data && !boot.data.needsSetup) return <Navigate to="/" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      signedIn(await http.post<MeDTO>("/api/setup", { ...values, setupCode: setupCode || undefined }));
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <h1>처음 오셨네요</h1>
      <p className="lead">처음 만드는 계정이 관리자가 됩니다. 다른 사람은 나중에 초대하세요.</p>
      <form onSubmit={submit} action="/api/setup" method="post" name="setup" id="setup-form">
        {err && !fieldError(err, "username") && !fieldError(err, "password") && !fieldError(err, "displayName") && !fieldError(err, "setupCode") ? <p className="form-error">{errorMessage(err)}</p> : null}
        {boot.data?.setupCodeRequired && (
          <Field id="setup-code" label="설정 코드" hint="서버 관리자가 정한 코드(SETUP_CODE)입니다." error={fieldError(err, "setupCode")}>
            <input id="setup-code" name="setup-code" className="input" autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false} required value={setupCode} onChange={(e) => setSetupCode(e.target.value)} aria-invalid={!!fieldError(err, "setupCode")} />
          </Field>
        )}
        <AccountFields err={err} values={values} set={set} />
        <button type="submit" className="btn btn-primary" disabled={busy}>
          관리자 계정 만들기
        </button>
      </form>
    </AuthShell>
  );
}

export function LoginPage() {
  const boot = useBootstrap();
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(params.get("error") ? (GOOGLE_ERRORS[params.get("error")!] ?? "로그인에 실패했습니다.") : null);
  const [busy, setBusy] = useState(false);
  const signedIn = useSignedIn();
  if (boot.data?.needsSetup) return <Navigate to="/setup" replace />;
  if (boot.data?.me) return <Navigate to="/" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      signedIn(await http.post<MeDTO>("/api/auth/login", { username, password }));
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <h1>로그인</h1>
      <p className="lead">계정이 없다면 관리자에게 초대 링크를 요청하세요.</p>
      {err && <p className="form-error">{err}</p>}
      {boot.data?.googleEnabled && !inNativeApp() && (
        <>
          <GoogleButton href="/auth/google/start?mode=login" />
          <div className="divider">또는 아이디로</div>
        </>
      )}
      <form onSubmit={submit} action="/api/auth/login" method="post" name="login" id="login-form">
        {/* 아이디에는 @가 들어갈 수 없다 (usernameSchema). 이메일로 몇 번 틀리면 로그인 횟수 제한에 걸린다 */}
        <Field id="username" label="아이디" error={username.includes("@") ? "이메일 주소가 아니라 가입할 때 정한 아이디를 입력하세요." : undefined}>
          <input id="username" name="username" type="text" className="input" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} required value={username} onChange={(e) => setUsername(e.target.value)} aria-invalid={username.includes("@")} />
        </Field>
        <Field id="current-password" label="비밀번호">
          <input id="current-password" name="password" type="password" className="input" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          로그인
        </button>
      </form>
    </AuthShell>
  );
}

const INVITE_REASONS: Record<string, string> = {
  used: "이미 사용한 초대 링크입니다.",
  expired: "기간이 지난 초대 링크입니다.",
  revoked: "관리자가 취소한 초대 링크입니다.",
  not_found: "올바르지 않은 초대 링크입니다.",
};

export function InvitePage() {
  const { token = "" } = useParams();
  const [params] = useSearchParams();
  const info = useInviteInfo(token);
  const boot = useBootstrap();
  const { values, set } = useAccountForm();
  const [err, setErr] = useState<unknown>(params.get("error") ? new Error(GOOGLE_ERRORS[params.get("error")!] ?? "가입에 실패했습니다.") : null);
  const [busy, setBusy] = useState(false);
  const signedIn = useSignedIn();
  if (boot.data?.me) return <Navigate to="/" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      signedIn(await http.post<MeDTO>(`/api/invites/${token}/accept`, values));
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  const data = info.data;
  const fieldErr = fieldError(err, "username") || fieldError(err, "password") || fieldError(err, "displayName");
  return (
    <AuthShell>
      {info.isPending ? (
        <p className="lead">초대 정보를 확인하는 중…</p>
      ) : !data?.valid ? (
        <>
          <h1>초대 링크를 쓸 수 없습니다</h1>
          <p className="lead">{INVITE_REASONS[data?.reason ?? "not_found"]} 관리자에게 새 초대를 요청하세요.</p>
          <a className="btn" href="/login">
            로그인 화면으로
          </a>
        </>
      ) : (
        <>
          <h1>JustOne에 초대받았습니다</h1>
          <p className="lead">{data.emailHint ? `${data.emailHint} 계정으로 초대받았습니다. 그 Google 계정으로 가입하세요.` : "Google 계정으로 가입하거나 아이디를 만드세요."}</p>
          {err && !fieldErr ? <p className="form-error">{errorMessage(err)}</p> : null}
          {data.googleEnabled && !inNativeApp() && <GoogleButton href={`/auth/google/start?mode=invite&invite=${encodeURIComponent(token)}`}>Google 계정으로 가입</GoogleButton>}
          {!data.emailHint && (
            <>
              {data.googleEnabled && !inNativeApp() && <div className="divider">또는 아이디로</div>}
              <form onSubmit={submit} action={`/api/invites/${token}/accept`} method="post" name="signup" id="signup-form">
                <AccountFields err={err} values={values} set={set} />
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  가입하기
                </button>
              </form>
            </>
          )}
        </>
      )}
    </AuthShell>
  );
}
